import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";

import ChevronDownIcon from "@/assets/icons/chevron-down.svg?react";
import SaveIcon from "@/assets/icons/save.svg?react";
import {
  ClientButton,
  ClientMetricsList,
  type ClientMetricsItem,
} from "@/components/clients";
import { clientPath, useClientBasePath } from "@/hooks/use-client-base-path";
import { useTciSession } from "@/hooks/use-tci-session";
import { useVisitorTracking } from "@/hooks/use-visitor-tracking";
import {
  buildTciReportPdfFilename,
  downloadElementAsPdf,
} from "@/lib/download-element-pdf";
import {
  formatMoney,
  formatTciScore,
  getMetricLabels,
  getStepQuestions,
  hasSectorFrictionQuestions,
  isWorkflowId,
  parseNumericInput,
  toRegionalSpelling,
  type RegionId,
} from "@/lib/tci";
import { cn } from "@/lib/utils";

/** Exact content width from Figma frame 849:8617 / 842:6872. */
const REPORT_RESULTS_WIDTH_PX = 890;

function formatWasteMoney(amount: number, regionId: RegionId | null): string {
  if (!Number.isFinite(amount) || amount <= 0) return formatMoney(0, regionId);
  return `+${formatMoney(amount, regionId)}`;
}

const TRAPPED_MARGIN_VALUE_CLASS = "text-[#22C55E]";

function formatCountInput(raw: string): string {
  const n = parseNumericInput(raw);
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n));
}

function formatPercentInput(value: number): string {
  return `${Math.round(value)}%`;
}

function formatCurrencyInput(raw: string, regionId: RegionId | null): string {
  const n = parseNumericInput(raw);
  if (!Number.isFinite(n)) return formatMoney(0, regionId);
  return formatMoney(n, regionId);
}

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

function ReportHighlightCard({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex w-full min-w-0 flex-col gap-[9px] border border-[rgba(56,189,248,0.35)] bg-[rgba(56,189,248,0.35)] backdrop-blur-[8px] sm:flex-1",
        className,
      )}
    >
      <span
        aria-hidden
        className="size-4 shrink-0 border-l-2 border-t-2 border-[#38bdf8]"
      />
      <div className="flex w-full flex-col gap-[9px] px-5">
        <p className="font-['Inter'] text-base font-medium leading-5 tracking-[-0.4px] text-white">
          {label}
        </p>
        <p className="font-['Inter'] text-[28px] font-normal leading-9 tracking-[-1.2px] text-white sm:text-[36px] sm:leading-[42px] sm:tracking-[-1.8px]">
          {value}
        </p>
      </div>
      <span className="flex w-full items-center justify-end">
        <span
          aria-hidden
          className="size-4 shrink-0 border-b-2 border-r-2 border-[#38bdf8]"
        />
      </span>
    </div>
  );
}

function BreakdownSection({
  title,
  items,
}: {
  title: string;
  items: ClientMetricsItem[];
}) {
  return (
    <div className="flex w-full flex-col gap-4">
      <p className="font-['Inter'] text-sm font-medium leading-5 tracking-[-0.35px] text-[rgba(255,255,255,0.50)]">
        {title}
      </p>
      <ClientMetricsList className="pb-6" items={items} />
    </div>
  );
}

function RecoveryLeverCard({
  leverLabel,
  title,
  description,
  reclaimable,
  regionId,
}: {
  leverLabel: string;
  title: string;
  description: string;
  reclaimable: string;
  regionId: RegionId | null;
}) {
  return (
    <div className="flex min-h-[237px] w-full flex-col justify-between border border-[rgba(56,189,248,0.35)] backdrop-blur-[8px] sm:min-w-0 sm:flex-1">
      <span
        aria-hidden
        className="size-4 shrink-0 border-l-2 border-t-2 border-[#38bdf8]"
      />
      <div className="flex w-full flex-col items-start px-5">
        <p className="w-full font-['Inter'] text-xs font-light uppercase leading-[22px] tracking-[-0.6px] text-[#38bdf8]">
          {toRegionalSpelling(leverLabel, regionId)}
        </p>
      </div>
      <div className="flex w-full flex-col gap-[9px] px-5 py-2.5">
        <p className="font-['Inter'] text-base font-medium leading-5 tracking-[-0.4px] text-white">
          {toRegionalSpelling(title, regionId)}
        </p>
        <p className="font-['Inter'] text-sm font-normal leading-[22px] tracking-[-0.7px] text-[rgba(255,255,255,0.80)]">
          {toRegionalSpelling(description, regionId)}
        </p>
      </div>
      <div className="flex w-full flex-col items-start px-5">
        <p className="font-['Inter'] text-[13px] font-medium leading-[22px] tracking-[-0.65px] text-[#22C55E]">
          {toRegionalSpelling("Reclaimable", regionId)}:&nbsp;
          <span className="font-bold">{reclaimable}</span>
        </p>
      </div>
      <span className="flex w-full items-center justify-end">
        <span
          aria-hidden
          className="size-4 shrink-0 border-b-2 border-r-2 border-[#38bdf8]"
        />
      </span>
    </div>
  );
}

/**
 * Full TCI report results screen (Figma 849:8617 expanded / 842:6872 collapsed).
 * Details are hidden by default. Values derived from session + calculateTci.
 */
export function ClientReportResultsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const basePath = useClientBasePath();
  const { session, result } = useTciSession();
  const { track } = useVisitorTracking();
  const reportRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const workflowParam = searchParams.get("workflow") ?? "";
  const workflowId = isWorkflowId(workflowParam) ? workflowParam : null;

  useEffect(() => {
    if (!workflowId || !result) return;
    track({
      currentStep: "results",
      lastEvent: "view_results",
      workflowId,
      laborWaste: result.laborWaste,
      yieldPenaltyWaste: result.yieldPenalty,
      untappedValue: result.untappedValue,
      totalTrapped: result.totalTrapped,
      tciScore: result.tciScore,
      bandId: result.band?.id ?? null,
      bandLabel: result.band?.label ?? null,
    });
  }, [workflowId, result, track]);

  if (!workflowId || !session || session.workflowId !== workflowId || !result) {
    return <Navigate to={basePath} replace />;
  }

  if (
    !session.businessSector ||
    !hasSectorFrictionQuestions(session.businessSector, workflowId)
  ) {
    return <Navigate to={basePath} replace />;
  }

  const activeWorkflowId = workflowId;
  const activeSession = session;
  const activeResult = result;
  const activeRegionId = activeSession.regionId;
  const metricLabels = getMetricLabels(activeRegionId);
  const questions = getStepQuestions(
    activeSession.businessSector,
    activeWorkflowId,
    activeRegionId,
  );
  const organisationName = activeSession.organisationName.trim() || "—";

  async function handleSavePdf() {
    const node = reportRef.current;
    if (!node || isDownloading) return;

    const wasExpanded = detailsExpanded;
    setIsDownloading(true);
    setDetailsExpanded(true);
    try {
      await waitForNextPaint();
      await downloadElementAsPdf(
        node,
        buildTciReportPdfFilename(activeWorkflowId),
      );
    } catch (error) {
      console.error("Failed to download TCI report PDF", error);
    } finally {
      setDetailsExpanded(wasExpanded);
      setIsDownloading(false);
    }
  }

  const TrappedMarginBreakdown: ClientMetricsItem[] = [
    {
      id: "labor-waste",
      label: metricLabels.labor.short,
      value: formatWasteMoney(activeResult.laborWaste, activeRegionId),
      valueClassName: TRAPPED_MARGIN_VALUE_CLASS,
    },
    {
      id: "yield-penalty",
      label: metricLabels.yieldPenalty.short,
      value: formatWasteMoney(activeResult.yieldPenalty, activeRegionId),
      valueClassName: TRAPPED_MARGIN_VALUE_CLASS,
    },
    {
      id: "untapped-value",
      label: metricLabels.untapped.short,
      value: formatWasteMoney(activeResult.untappedValue, activeRegionId),
      valueClassName: TRAPPED_MARGIN_VALUE_CLASS,
    },
    {
      id: "trapped-margin",
      label: "Total Trapped Margin",
      value: formatMoney(activeResult.totalTrapped, activeRegionId),
      valueClassName: TRAPPED_MARGIN_VALUE_CLASS,
      emphasized: true,
    },
  ];

  const laborBreakdown: ClientMetricsItem[] = [
    {
      id: questions.labor[0].id,
      label: questions.labor[0].label,
      value: formatCountInput(activeSession.labor.headcount),
    },
    {
      id: questions.labor[1].id,
      label: questions.labor[1].label,
      value: formatPercentInput(activeSession.labor.timePercent),
    },
    {
      id: questions.labor[2].id,
      label: questions.labor[2].label,
      value: formatCurrencyInput(activeSession.labor.salary, activeRegionId),
    },
  ];

  const yieldBreakdown: ClientMetricsItem[] = [
    {
      id: questions.yieldPenalty[0].id,
      label: questions.yieldPenalty[0].label,
      value: formatCountInput(activeSession.yieldPenalty.volume),
    },
    {
      id: questions.yieldPenalty[1].id,
      label: questions.yieldPenalty[1].label,
      value: formatCurrencyInput(
        activeSession.yieldPenalty.penaltyPerEvent,
        activeRegionId,
      ),
    },
  ];

  const untappedBreakdown: ClientMetricsItem[] = [
    {
      id: questions.untapped[0].id,
      label: questions.untapped[0].label,
      value: formatCountInput(activeSession.untapped.volume),
    },
    {
      id: questions.untapped[1].id,
      label: questions.untapped[1].label,
      value: formatPercentInput(activeSession.untapped.actionablePercent),
    },
    {
      id: questions.untapped[2].id,
      label: questions.untapped[2].label,
      value: formatCurrencyInput(
        activeSession.untapped.valuePerAction,
        activeRegionId,
      ),
    },
  ];

  return (
    <section
      className="mx-auto flex w-full flex-col items-start gap-10"
      style={{ maxWidth: REPORT_RESULTS_WIDTH_PX }}
    >
      <div ref={reportRef} className="flex w-full flex-col items-start gap-10">
        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full items-start justify-between gap-4">
            <h1 className="min-w-0 max-w-[447px] font-['Inter'] text-[28px] font-normal leading-9 tracking-[-1.2px] text-white sm:text-4xl sm:leading-[44px] sm:tracking-[-1.8px] lg:text-[48.4px] lg:leading-[56.4px] lg:tracking-[-2.82px]">
              <span className="block text-[#38bdf8]">{organisationName}</span>
              <span className="block">Trapped Margin Breakdown</span>
            </h1>
            <button
              type="button"
              disabled={isDownloading}
              aria-busy={isDownloading}
              onClick={() => {
                void handleSavePdf();
              }}
              className={cn(
                "inline-flex shrink-0 items-center gap-2 border border-[rgba(56,189,248,0.35)] bg-[rgba(11,22,40,0.42)] px-5 py-3 font-['Inter'] text-sm font-medium leading-5 tracking-[-0.35px] text-white backdrop-blur-[8px] transition-colors",
                "hover:border-[#38bdf8] hover:bg-[rgba(56,189,248,0.18)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black",
                "disabled:pointer-events-none disabled:opacity-50",
              )}
            >
              <SaveIcon aria-hidden className="size-[18px] shrink-0" />
              {isDownloading ? "Preparing…" : "Save PDF"}
            </button>
          </div>

          <div className="flex w-full flex-col gap-[14px] sm:flex-row sm:items-stretch">
            <ReportHighlightCard
              label="Total Trapped Margin"
              value={formatMoney(activeResult.totalTrapped, activeRegionId)}
            />
            <ReportHighlightCard
              label="TCI Score (%)"
              value={formatTciScore(activeResult.tciScore)}
            />
          </div>
        </div>

        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full items-center justify-between gap-3">
            <h2 className="min-w-0 flex-1 font-['Inter'] text-base font-medium leading-5 tracking-[-0.4px] text-white">
              Trapped Margin Breakdown
            </h2>
            <button
              type="button"
              aria-expanded={detailsExpanded}
              onClick={() => setDetailsExpanded((open) => !open)}
              className="inline-flex shrink-0 items-center gap-2 border border-[rgba(56,189,248,0.35)] bg-[rgba(11,22,40,0.42)] px-3 py-2 font-['Inter'] text-sm font-semibold leading-normal text-[#38bdf8] transition-colors hover:border-[#38bdf8] hover:bg-[rgba(56,189,248,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {detailsExpanded ? "Hide Details" : "Show Details"}
              <ChevronDownIcon
                aria-hidden
                className={cn(
                  "size-[14px] shrink-0 text-[#38bdf8] transition-transform",
                  detailsExpanded && "rotate-180",
                )}
              />
            </button>
          </div>

          {detailsExpanded ? (
            <div className="flex w-full flex-col gap-[50px]">
              <ClientMetricsList
                aria-label="Trapped Margin Breakdown"
                items={TrappedMarginBreakdown}
              />

              <div className="flex w-full flex-col gap-6">
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <p className="font-['Inter'] text-base font-medium leading-5 tracking-[-0.4px] text-white">
                    Business Sector:{" "}
                    <span className="text-[#38bdf8]">
                      {activeSession.businessSector ?? "—"}
                    </span>
                  </p>
                  <p className="font-['Inter'] text-base font-medium leading-5 tracking-[-0.4px] text-[rgba(255,255,255,0.50)]">
                    Diagnostic Variables Breakdown
                  </p>
                </div>

                <BreakdownSection
                  title={`1. ${metricLabels.labor.short}`}
                  items={laborBreakdown}
                />
                <BreakdownSection
                  title={`2. ${metricLabels.yieldPenalty.short}`}
                  items={yieldBreakdown}
                />
                <BreakdownSection
                  title={`3. ${metricLabels.untapped.short}`}
                  items={untappedBreakdown}
                />
              </div>
            </div>
          ) : (
            <div className="flex h-16 w-full items-center justify-center border border-[rgba(56,189,248,0.35)] bg-[rgba(11,22,40,0.42)]">
              <p className="font-['Inter'] text-sm font-normal leading-normal text-[#9194a1]">
                Detailed breakdown hidden by default
              </p>
            </div>
          )}
        </div>

        <div className="flex w-full flex-col gap-6">
          <h2 className="font-['Inter'] text-base font-medium leading-5 tracking-[-0.4px] text-white">
            Capacity Recovery Strategy
          </h2>
          <div className="flex w-full flex-col gap-4">
            <p className="font-['Inter'] text-sm font-normal leading-normal tracking-[-1.2px] text-[#9194a1]">
              How you can convert Trapped Margin into EBITDA
            </p>
            <div className="flex w-full flex-col gap-[14px] lg:flex-row lg:items-stretch">
              <RecoveryLeverCard
                regionId={activeRegionId}
                leverLabel="Lever 01 • Labor Reclamation"
                title="Automated Data Pipeline"
                description="Deploy AI worker agents to handle repetitive email-to-CRM data entry and manual document matching, returning up to 80% of lost repetitive hours to high-value client work."
                reclaimable={formatMoney(
                  activeResult.laborWaste,
                  activeRegionId,
                )}
              />
              <RecoveryLeverCard
                regionId={activeRegionId}
                leverLabel="Lever 02 • Yield Acceleration"
                title="Instant Quote Engine"
                description="Automate pricing matrix rules and approval routing to reduce quote turnaround from days to minutes, preventing competitive yield loss on high-value proposals."
                reclaimable={formatMoney(
                  activeResult.yieldPenalty,
                  activeRegionId,
                )}
              />
              <RecoveryLeverCard
                regionId={activeRegionId}
                leverLabel="Lever 03 • Database Mining"
                title="Autonomous Nurture AI"
                description="Continuously scan idle database leads and historic tickets, activating dormant contracts and past opportunities without increasing headcount."
                reclaimable={formatMoney(
                  activeResult.untappedValue,
                  activeRegionId,
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-auto w-full flex-col gap-2 sm:h-[46px] sm:flex-row sm:items-center sm:gap-2">
        <ClientButton
          variant="secondary"
          type="button"
          className="sm:w-auto sm:min-w-0"
          title="Check readiness for AI"
          onClick={() => {
            window.alert("Coming soon.");
          }}
        >
          Check Readiness for AI
        </ClientButton>
        <div className="flex min-w-0 flex-1 sm:justify-end">
          <ClientButton
            variant="primary"
            type="button"
            title="See your reclaimable ROI"
            onClick={() => {
              track({
                currentStep: "results",
                lastEvent: "view_investment_roi",
                workflowId: activeWorkflowId,
              });
              navigate(
                `${clientPath(basePath, "calculator/investment")}?workflow=${encodeURIComponent(activeWorkflowId)}`,
              );
            }}
          >
            See Your Reclaimable ROI
          </ClientButton>
        </div>
      </div>
    </section>
  );
}
